// Chart-type registry — twasta's analog of insight-bi's Store.ts.
//
// Maps a catalog identity (the visualLayout `className` like "InsightColumnChart",
// or the `ID` like "column") to the vanilla-JS chart we instantiate: the global
// it defines on window, plus the ordered vendored dependencies the loader must
// inject first. New chart types are registered here as they are vendored.

export type NativeKind =
  | 'table' | 'pivot' | 'cards' | 'feeds' | 'html' | 'xsl'
  | 'calendar' | 'tree' | 'gantt' | 'combo' | 'map' | 'document'

export interface ChartTypeDef {
  /** A native React renderer (no vendored JS chart class). When set,
   *  `global`/`deps` are ignored and InsightVisual renders the mapped
   *  component instead. */
  native?: NativeKind
  /** window global the vendored script defines (the class we `new`). */
  global?: string
  /** Vendored globals to ensure-load, in order (deps first). */
  deps?: string[]
  /**
   * Whether the generic in-visual drill-down (Phase-7 `drilldown.ts`) may
   * re-render this chart one level deeper when its category group has >1
   * dimension. True for the cartesian/pie/funnel/radar/heatmap family; false
   * for treemap (native Plotly pathbar hierarchy), sankey, gauge, and map;
   * unset (falsy) for native table/pivot (they already show all columns).
   */
  hierDrill?: boolean
}

// Keyed by BOTH the catalog className and the catalog ID so either resolves.
// `deps` lists the vendored globals to inject, in order (BaseVisual first).
const REGISTRY: Record<string, ChartTypeDef> = {
  // Column
  InsightColumnChart: { global: 'ColumnChart', deps: ['BaseVisual', 'ColumnChart'], hierDrill: true },
  column: { global: 'ColumnChart', deps: ['BaseVisual', 'ColumnChart'], hierDrill: true },
  // Bar (horizontal)
  InsightBarChart: { global: 'BarChart', deps: ['BaseVisual', 'BarChart'], hierDrill: true },
  bar_h: { global: 'BarChart', deps: ['BaseVisual', 'BarChart'], hierDrill: true },
  bar: { global: 'BarChart', deps: ['BaseVisual', 'BarChart'], hierDrill: true },
  // Line
  InsightLineChart: { global: 'LineChart', deps: ['BaseVisual', 'LineChart'], hierDrill: true },
  line: { global: 'LineChart', deps: ['BaseVisual', 'LineChart'], hierDrill: true },
  // Pie
  InsightPieChart: { global: 'PieChart', deps: ['BaseVisual', 'PieChart'], hierDrill: true },
  pie: { global: 'PieChart', deps: ['BaseVisual', 'PieChart'], hierDrill: true },
  // Doughnut — NOT a pie with a hole: it is the target-vs-achievement ring
  // widget its catalog groups describe (Total/Achievement Value 1-3 + Footer).
  // Aliasing it to PieChart made it read rows/values, groups it doesn't have,
  // so it rendered nothing. No hierarchy to drill: each ring is one number.
  InsightDoughnut: { global: 'DoughnutChart', deps: ['BaseVisual', 'DoughnutChart'], hierDrill: false },
  doughnut: { global: 'DoughnutChart', deps: ['BaseVisual', 'DoughnutChart'], hierDrill: false },
  // Scatter
  InsightScatterChart: { global: 'ScatterPlot', deps: ['BaseVisual', 'ScatterPlot'], hierDrill: true },
  scatter: { global: 'ScatterPlot', deps: ['BaseVisual', 'ScatterPlot'], hierDrill: true },
  // Stacked Column
  InsightStackedChart: { global: 'StackedColumnChart', deps: ['BaseVisual', 'StackedColumnChart'], hierDrill: true },
  stacked_column: { global: 'StackedColumnChart', deps: ['BaseVisual', 'StackedColumnChart'], hierDrill: true },
  // Column + Line combo
  InsightColumnLineChart: { global: 'ColumnLineChart', deps: ['BaseVisual', 'ColumnLineChart'], hierDrill: true },
  column_line: { global: 'ColumnLineChart', deps: ['BaseVisual', 'ColumnLineChart'], hierDrill: true },
  // Bubble
  InsightBubbleChart: { global: 'BubbleChart', deps: ['BaseVisual', 'BubbleChart'], hierDrill: true },
  'insight-bubble-chart': { global: 'BubbleChart', deps: ['BaseVisual', 'BubbleChart'], hierDrill: true },
  bubble: { global: 'BubbleChart', deps: ['BaseVisual', 'BubbleChart'], hierDrill: true },
  // Heatmap
  InsightHeatmap: { global: 'Heatmap', deps: ['BaseVisual', 'Heatmap'], hierDrill: true },
  'insight-heatmap': { global: 'Heatmap', deps: ['BaseVisual', 'Heatmap'], hierDrill: true },
  heatmap: { global: 'Heatmap', deps: ['BaseVisual', 'Heatmap'], hierDrill: true },
  // Gauge (Plotly indicator, gauge+number with optional delta vs Target)
  InsightGauge: { global: 'GaugeChart', deps: ['BaseVisual', 'GaugeChart'], hierDrill: false },
  insight_gauge: { global: 'GaugeChart', deps: ['BaseVisual', 'GaugeChart'], hierDrill: false },
  // Treemap
  InsightTreemap: { global: 'TreemapChart', deps: ['BaseVisual', 'TreemapChart'], hierDrill: false },
  insight_treemap: { global: 'TreemapChart', deps: ['BaseVisual', 'TreemapChart'], hierDrill: false },
  // Funnel
  InsightFunnel: { global: 'FunnelChart', deps: ['BaseVisual', 'FunnelChart'], hierDrill: true },
  insight_funnel: { global: 'FunnelChart', deps: ['BaseVisual', 'FunnelChart'], hierDrill: true },
  // Waterfall
  InsightWaterfall: { global: 'WaterfallChart', deps: ['BaseVisual', 'WaterfallChart'] },
  insight_waterfall: { global: 'WaterfallChart', deps: ['BaseVisual', 'WaterfallChart'] },
  // Radar (filled scatterpolar)
  InsightRadar: { global: 'RadarChart', deps: ['BaseVisual', 'RadarChart'], hierDrill: true },
  insight_radar: { global: 'RadarChart', deps: ['BaseVisual', 'RadarChart'], hierDrill: true },
  // Sankey
  InsightSankey: { global: 'SankeyChart', deps: ['BaseVisual', 'SankeyChart'], hierDrill: false },
  insight_sankey: { global: 'SankeyChart', deps: ['BaseVisual', 'SankeyChart'], hierDrill: false },
  // Grid / Tabular — native HTML table (no Flexmonster).
  InsightGridTable: { native: 'table' },
  grid: { native: 'table' },
  InsightTabular: { native: 'table' },
  insight_tabular: { native: 'table' },
  // Pivot — native cross-tab (falls back to a flat table without a column dim).
  InsightPivotTable: { native: 'pivot' },
  pivot: { native: 'pivot' },
  // Cards / KPI
  InsightCards: { native: 'cards' },
  cards: { native: 'cards' },
  // Feeds
  InsightFeeds: { native: 'feeds' },
  feeds: { native: 'feeds' },
  // Html — render the content column.
  Insighthtml: { native: 'html' },
  html: { native: 'html' },
  // XSL — serialize the rows to XML and run the author's `xslTemplate`
  // stylesheet over them. Falls back to the Html view when none is set.
  Insightxsl: { native: 'xsl' },
  xml: { native: 'xsl' },
  // Calendar feed
  InsightCalendarFeed: { native: 'calendar' },
  insight_calendar_feed: { native: 'calendar' },
  // Tree / Org chart
  InsightTree: { native: 'tree' },
  insight_tree: { native: 'tree' },
  InsightTreeOrgchart: { native: 'tree' },
  insight_tree_orgchart: { native: 'tree' },
  // Task scheduler (Gantt)
  InsightTaskScheduler: { native: 'gantt' },
  insight_task_scheduler: { native: 'gantt' },
  // MultiStackLine (stacked bars + line)
  InsightStackbarLinechart: { native: 'combo' },
  insight_stackbar_linechart: { native: 'combo' },
  // Map
  InsightMap: { native: 'map' },
  insight_map: { native: 'map' },
  map: { native: 'map' },
  // Document — a laid-out document over the rows, drawn by the document
  // engine rather than by anything in this package.
  //
  // Registered with a PLACEHOLDER renderer rather than left out, and the
  // difference matters in two places: `isSupported` would otherwise call a
  // perfectly good visual unsupported, and a host that has not been taught to
  // dispatch (the assistant's inline chart, a thumbnail) would fall through to
  // an error where the honest answer is "open it as a document". The real
  // renderer cannot live here — it carries the TipTap editor, and pulling that
  // into this package puts it in every bundle that draws a chart.
  InsightDocument: { native: 'document' },
  insight_document: { native: 'document' },
  document: { native: 'document' },
}

/** Resolve a chart definition from a className / ID (case-insensitive on ID). */
export function resolveChartType(
  ...candidates: Array<string | undefined | null>
): ChartTypeDef | null {
  for (const raw of candidates) {
    if (!raw) continue
    const key = String(raw).trim()
    if (REGISTRY[key]) return REGISTRY[key]
    if (REGISTRY[key.toLowerCase()]) return REGISTRY[key.toLowerCase()]
  }
  return null
}

/** True when a chart type is renderable (registered + vendored). */
export function isSupported(...candidates: Array<string | undefined | null>): boolean {
  return resolveChartType(...candidates) !== null
}

/**
 * Is this visual's output a laid-out DOCUMENT rather than a chart?
 *
 * The ONE answer, because five places ask: the visual designer (off the
 * catalog entry), three render hosts and the BI verify gate (off the saved
 * layout). They were comparing the exact string `'InsightDocument'`
 * separately, which meant an ID-spelled model (`insight_document`) dispatched
 * as a chart in some of them and as a document in others, and a catalog
 * rename would split them again. Resolving through the registry accepts every
 * spelling the registry accepts.
 */
export function isDocumentVisual(
  ...candidates: Array<string | undefined | null>
): boolean {
  return resolveChartType(...candidates)?.native === 'document'
}

/** All registered identities (for diagnostics / "supported types" UI). */
export function registeredKeys(): string[] {
  return Object.keys(REGISTRY)
}
